import React, { useEffect, useRef, useState } from "react";
import { FiEdit2, FiEye, FiEyeOff, FiPlus, FiSearch, FiTrash2, FiUpload, FiX } from "react-icons/fi";
import { useSelector } from "react-redux";
import api, { apiError } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import TableComp from "../../appcomponents/TableComp";
import PaginationComp from "../../appcomponents/PaginationComp";
import ConfirmModal from "../../Modal/ConfirmModal";
import useDebounce from "../../hooks/useDebounce";
import { hasPermission, selectUser } from "../../store/authSlice";
import styles from "./Users.module.css";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role_id: "",
  is_super: false,
};

export default function Users() {
  const currentUser = useSelector(selectUser);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewUser, setViewUser] = useState(null);
  const imageInputRef = useRef(null);
  const debouncedSearch = useDebounce(search, 350);
  const canAdd = hasPermission(currentUser, "users", "add");
  const canView = hasPermission(currentUser, "users", "view");
  const canEdit = hasPermission(currentUser, "users", "edit");
  const canDelete = hasPermission(currentUser, "users", "delete");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        api.get("/api/users.php", { params: { search: debouncedSearch, page, per_page: 10 } }),
        api.get("/api/roles.php", { params: { per_page: 200 } }),
      ]);
      const payload = usersRes.data?.data || {};
      setUsers(payload.items || []);
      setTotal(payload.total || 0);
      setRoles(rolesRes.data?.data?.items || []);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  useEffect(() => {
    if (!modalOpen) {
      setEditingUser(null);
      setForm((prev) => (JSON.stringify(prev) === JSON.stringify(emptyForm) ? prev : emptyForm));
      setImageFile(null);
      setImagePreview("");
      setImageError(false);
      setShowPassword(false);
      return;
    }
    if (editingUser) {
      const nextForm = {
        name: editingUser.name || "",
        email: editingUser.email || "",
        password: "",
        role_id: editingUser.role_id ? String(editingUser.role_id) : "",
        is_super: Boolean(editingUser.is_super),
      };
      setForm((prev) => (JSON.stringify(prev) === JSON.stringify(nextForm) ? prev : nextForm));
      setImagePreview((prev) => (prev === (editingUser.image_url || "") ? prev : (editingUser.image_url || "")));
      setImageError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, editingUser?.id]);

  useEffect(() => {
    if (!imageFile) return;
    setUploading(true);
    const preview = URL.createObjectURL(imageFile);
    setImagePreview(preview);
    RenderToast({ type: "success", message: "Image attached successfully." });
    const timer = window.setTimeout(() => setUploading(false), 350);
    return () => {
      URL.revokeObjectURL(preview);
      window.clearTimeout(timer);
    };
  }, [imageFile]);

  const openCreate = () => {
    if (!canAdd) return;
    setEditingUser(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageError(false);
      setImageFile(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setImageError(false);
    RenderToast({ type: "success", message: "Image removed." });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      RenderToast({ type: "error", message: "Name is required." });
      return;
    }
    if (!form.email.trim()) {
      RenderToast({ type: "error", message: "Email is required." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      RenderToast({ type: "error", message: "Enter a valid email address." });
      return;
    }
    if (!form.role_id) {
      RenderToast({ type: "error", message: "Role is required." });
      return;
    }
    if (!editingUser && !form.password) {
      RenderToast({ type: "error", message: "Password is required." });
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append("action", editingUser ? "update" : "add");
      if (editingUser?.id) data.append("id", editingUser.id);
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("password", form.password);
      data.append("role_id", form.role_id);
      if (currentUser?.is_super) {
        data.append("is_super", form.is_super ? "1" : "0");
      }
      if (imageFile) data.append("image", imageFile);

      const response = await api.post("/api/users.php", data);
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to save user.");
      }
      RenderToast({
        type: "success",
        message: editingUser ? "User updated successfully." : "User created successfully.",
      });
      setModalOpen(false);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const response = await api.post("/api/users.php", {
        action: "delete",
        id: deleteTarget.id,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to delete user.");
      }
      RenderToast({ type: "success", message: "User deleted successfully." });
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleteLoading(true);
    try {
      const results = await Promise.all(
        selectedIds.map((id) => api.post("/api/users.php", { action: "delete", id }))
      );
      if (results.some((res) => !res.data?.success)) {
        throw new Error("One or more users could not be deleted.");
      }
      RenderToast({ type: "success", message: "Selected users deleted successfully." });
      setSelectedIds([]);
      fetchData();
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setDeleteLoading(false);
    }
  };

  const allSelected = users.length > 0 && selectedIds.length === users.length;

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => setSelectedIds(allSelected ? [] : users.map((user) => user.id))}
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.id)}
          onChange={() =>
            setSelectedIds((prev) =>
              prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
            )
          }
        />
      ),
    },
    {
      key: "image",
      label: "Image",
      render: (row) =>
        row.image_url ? (
          <img className={styles.avatar} src={row.image_url} alt={row.name} />
        ) : (
          "-"
        ),
    },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "username", label: "Username" },
    { key: "role_name", label: "Role" },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <div className={styles.actions}>
          {canView ? (
            <button type="button" onClick={() => setViewUser(row)} title="View">
              <FiEye />
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" onClick={() => openEdit(row)} title="Edit">
              <FiEdit2 />
            </button>
          ) : null}
          {canDelete ? (
            <button type="button" onClick={() => setDeleteTarget(row)} title="Delete">
              <FiTrash2 />
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1>Users</h1>
          <p>Create admin users and assign roles.</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch />
          <input
            type="search"
            value={search}
            placeholder="Search users..."
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
          />
        </div>
        <div className={styles.toolbarActions}>
          {selectedIds.length > 0 && canDelete && (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={handleBulkDelete}
              disabled={deleteLoading}
            >
              <FiTrash2 />
              {deleteLoading ? "Deleting..." : "Delete Selected"}
            </button>
          )}
          {canAdd ? (
            <button type="button" className={styles.addBtn} onClick={openCreate}>
              <FiPlus />
              Add User
            </button>
          ) : null}
        </div>
      </div>

      <TableComp columns={columns} rows={users} loading={loading} emptyMessage="No users found." />

      <div className={styles.paginationRow}>
        <PaginationComp total={total} perPage={10} currentPage={page} onPageChange={setPage} />
      </div>

      {modalOpen && (
        <div className={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>{editingUser ? "Edit User" : "Add User"}</h3>
            </div>
            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.fullWidth}>
                <span>Name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                />
              </label>
              <label className={styles.fullWidth}>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="name@example.com"
                />
              </label>
              <label>
                <span>Password {editingUser ? "(leave blank to keep current)" : ""}</span>
                <div className={styles.passwordField}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder="Password"
                  />
                  <button type="button" onClick={() => setShowPassword((prev) => !prev)}>
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>
              <label>
                <span>Role</span>
                <select
                  value={form.role_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, role_id: e.target.value }))}
                >
                  <option value="">Select role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </label>
              {currentUser?.is_super ? (
                <label className={`${styles.fullWidth} ${styles.superAdminRow}`}>
                  <span className={styles.superAdminCheck}>
                    <input
                      type="checkbox"
                      checked={form.is_super}
                      disabled={editingUser?.id === currentUser?.id}
                      onChange={(e) => setForm((prev) => ({ ...prev, is_super: e.target.checked }))}
                    />
                    <span>Super Admin (full access, bypasses all permission checks)</span>
                  </span>
                  {editingUser?.id === currentUser?.id ? (
                    <small>You can't change your own Super Admin status.</small>
                  ) : null}
                </label>
              ) : null}
              <div className={styles.uploadBox}>
                <span className={styles.label}>Profile Image</span>
                <div
                  className={`${styles.preview} ${imagePreview && !imageError ? styles.previewHasImage : styles.previewEmpty}`}
                  onClick={() => imageInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      imageInputRef.current?.click();
                    }
                  }}
                >
                  {uploading ? (
                    <div className={styles.spinner} />
                  ) : imagePreview && !imageError ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        onError={() => setImageError(true)}
                      />
                      <button
                        type="button"
                        className={styles.removeImageBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage();
                        }}
                      >
                        <FiX />
                      </button>
                    </>
                  ) : (
                    <div className={styles.previewFallback}>
                      <span className={styles.previewFallbackLetter}>
                        {form.name?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                      <span className={styles.previewFallbackText}>Click to upload</span>
                    </div>
                  )}
                  <div className={styles.previewOverlay}>
                    <span className={styles.previewOverlayIcon}>
                      <FiUpload />
                    </span>
                    <span>Upload image</span>
                  </div>
                </div>
                <p className={styles.previewNote}>Recommended size: 300x300px. JPG, PNG or WEBP.</p>
                <input
                  ref={imageInputRef}
                  id="user-image-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className={styles.hiddenInput}
                />
              </div>
              <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryBtn} onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className={styles.primaryBtn} disabled={saving || uploading}>
                  {saving ? "Saving..." : editingUser ? "Update User" : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewUser && (
        <div className={styles.modalOverlay} onClick={() => setViewUser(null)}>
          <div className={styles.viewModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <h3>User Details</h3>
            </div>
            <div className={styles.viewBody}>
              <div className={styles.viewAvatar}>{viewUser.image_url ? <img src={viewUser.image_url} alt={viewUser.name} /> : viewUser.name?.charAt(0)}</div>
              <div className={styles.viewRow}><span>Name</span><strong>{viewUser.name}</strong></div>
              <div className={styles.viewRow}><span>Email</span><strong>{viewUser.email || "-"}</strong></div>
              <div className={styles.viewRow}><span>Username</span><strong>{viewUser.username}</strong></div>
              <div className={styles.viewRow}><span>Role</span><strong>{viewUser.role_name || "-"}</strong></div>
              <div className={styles.viewRow}><span>Super Admin</span><strong>{viewUser.is_super ? "Yes" : "No"}</strong></div>
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setViewUser(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteTarget?.name || "this user"}?`}
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
